import type { Socket } from 'socket.io-client'
import { throttle } from 'lodash-es'
import { ref } from 'vue'
import { Model, ServerUpdate } from '../server-src/model'
import { interpolateState, extrapolateState } from './interpolate'

let RENDER_DELAY = ref(
  // JSON.parse(localStorage.getItem('RENDER_DELAY') ?? 'null') ?? -400
  50
)
// let sock: SocketIOClient.Socket | null
// document.addEventListener('keyup', (e) => {
//   if (e.key === 'i') {
//     RENDER_DELAY.value += 2
//   } else if (e.key === 'k') {
//     RENDER_DELAY.value -= 2
//   } else if (e.key === 'u') {
//     sock?.emit('u')
//   } else if (e.key === 'j') {
//     sock?.emit('j')
//   }
// })
// const sendTime = ref(10)
// watchEffect(() => {
//   document.title = sendTime.value + ' ' + RENDER_DELAY.value
// })

// input lag is less bearable on a touch screen
let isDefinitelyKeyboard = false
function markAsTouch() {
  if (isDefinitelyKeyboard) return
  RENDER_DELAY.value = 10
}
function markAsKeyboard() {
  isDefinitelyKeyboard = true
  RENDER_DELAY.value = 50
}
document.addEventListener('touchstart', markAsTouch, {
  once: true,
  passive: true,
})

const forDebugging = { timesInterpolated: 0, timesExtrapolated: 0 }
let debugCorrectiveFactorBaseline: number | undefined = undefined

setInterval(() => {
  localStorage.setItem('RENDER_DELAY', JSON.stringify(RENDER_DELAY.value))
}, 20000)

const log = throttle((a) => {
  console.log(JSON.stringify(a, undefined, 2))
}, 1000)

const NUM_IN_ROLLING_AVERAGE = 30
class RollingAverageCalculator {
  private arr: number[] = []
  private sum = NaN
  get value(): number {
    return this.sum / this.len()
  }
  get debugArrLen(): number {
    return this.arr.length
  }

  private len(): number {
    return Math.min(this.arr.length, NUM_IN_ROLLING_AVERAGE)
  }

  public addValue(newValue: number) {
    if (isNaN(this.sum)) {
      this.sum = newValue
    } else {
      // add new value
      this.sum += newValue
    }
    this.arr.push(newValue)
    if (this.arr.length > NUM_IN_ROLLING_AVERAGE) {
      // remove oldest value that's currently in the average
      // TODO: check that this is the right index
      this.sum -= this.arr[this.arr.length - NUM_IN_ROLLING_AVERAGE - 1]
    }
    if (this.arr.length > 10 * NUM_IN_ROLLING_AVERAGE) {
      // console.log('splicing!')
      this.arr.splice(0, 8 * NUM_IN_ROLLING_AVERAGE)
    }
  }
}

function changeAtSpeed(
  speed: number,
  oldVal: number,
  newVal: number,
  oldTime: number,
  newTime: number
): number {
  const difference = newVal - oldVal
  const absDifference = Math.abs(difference)
  const differenceSign = Math.sign(difference)
  const maxAmountToChange = Math.abs((newTime - oldTime) * speed)
  if (absDifference < maxAmountToChange) {
    return newVal
  } else {
    // console.log('max speed! ' + differenceSign)
    return oldVal + differenceSign * maxAmountToChange
  }
}

// decrease quickly, increase slowly
class SlowMovingRollingAverageCalculator {
  private rollingAverageCalculator = new RollingAverageCalculator()
  private timeUpdated = NaN
  public value = NaN
  private SLOW_SPEED = 50 / 1000 // (milliseconds to change)/second
  private MIN_AMOUNT_TO_CHANGE_SLOW = 2
  private FAST_SPEED = 200 / 1000
  private IMMEDIATE_CHANGE_THRESHOLD = 1000 / 1000

  public addValue(newValue: number) {
    this.rollingAverageCalculator.addValue(newValue)
    const oldVal = this.value
    const newVal = this.rollingAverageCalculator.value
    const newTimeUpdated = performance.now()
    if (
      isNaN(oldVal) ||
      isNaN(this.timeUpdated) ||
      Math.abs(oldVal - newVal) > this.IMMEDIATE_CHANGE_THRESHOLD
    ) {
      this.value = newVal
    } else if (newVal <= oldVal) {
      // decrease quickly
      this.value = changeAtSpeed(
        this.FAST_SPEED,
        oldVal,
        newVal,
        this.timeUpdated,
        newTimeUpdated
      )
    } else if (Math.abs(oldVal - newVal) > this.MIN_AMOUNT_TO_CHANGE_SLOW) {
      // console.log('increasing!')
      // increase slowly
      this.value = changeAtSpeed(
        this.SLOW_SPEED,
        oldVal,
        newVal,
        this.timeUpdated,
        newTimeUpdated
      )
    } else {
      // console.log('not increasing yet!')
    }
    this.timeUpdated = newTimeUpdated
  }

  get debugArrLen() {
    return this.rollingAverageCalculator.debugArrLen
  }
}

let serverId: string | undefined

export class StateGetter {
  private gameUpdates: ServerUpdate[] = []
  private correctiveFactorCalculator = new SlowMovingRollingAverageCalculator()
  private reset() {
    this.correctiveFactorCalculator = new SlowMovingRollingAverageCalculator()
    this.gameUpdates = []
  }
  // (server time when update sent) - (client time when update received)
  private get correctiveFactor(): number {
    return this.correctiveFactorCalculator.value
  }
  // private gameStart = 0
  // private firstServerTimestamp = 0

  constructor(socket: Socket) {
    // sock = socket
    // sock.on('sendTime', (t: number) => (sendTime.value = t))
    socket.on('model', (mod: ServerUpdate) => {
      if (serverId == undefined) serverId = mod.serverId
      if (mod.serverId !== serverId) {
        serverId = undefined
        console.log('Server id has changed. Resetting!')
        this.reset()
      }
      this.processGameUpdate(mod)
    })
    socket.on('disconnect', () => {
      console.log('disconnect')
    })
    socket.on('connect', () => {
      console.log('connect')
    })
  }

  private processGameUpdate(update: ServerUpdate) {
    // if (!this.firstServerTimestamp) {
    //   this.firstServerTimestamp = update.t
    //   this.gameStart = performance.now()
    //   console.log(this.firstServerTimestamp - this.gameStart)
    // }
    this.gameUpdates.push(update)
    this.correctiveFactorCalculator.addValue(
      /* server time */ update.t - /* client time */ performance.now()
    )
    this.gameUpdates.push(update)

    // Keep only one game update before the current server time
    const base = this.baseUpdate
    if (base > 0) {
      this.gameUpdates.splice(0, base)
    }
  }

  private get currentServerTime() {
    // return (
    //   this.firstServerTimestamp +
    //   (performance.now() - this.gameStart) -
    //   RENDER_DELAY.value
    // )
    return performance.now() + this.correctiveFactor - RENDER_DELAY.value
  }

  // Returns the index of the base update, the first game update before
  // current server time, or -1 if N/A.
  private get baseUpdate() {
    const serverTime = this.currentServerTime
    for (let i = this.gameUpdates.length - 1; i >= 0; i--) {
      if (this.gameUpdates[i].t <= serverTime) {
        return i
      }
    }
    return -1
  }

  get currentState(): Model | null {
    // if (!this.firstServerTimestamp) {
    if (isNaN(this.correctiveFactor)) {
      return null
    }

    const base = this.baseUpdate
    const serverTime = this.currentServerTime

    if (
      debugCorrectiveFactorBaseline == null &&
      !isNaN(this.correctiveFactor)
    ) {
      debugCorrectiveFactorBaseline = this.correctiveFactor
    }
    log({
      RENDER_DELAY: RENDER_DELAY.value,
      correctiveFactor:
        this.correctiveFactor - (debugCorrectiveFactorBaseline ?? 0),
      arrLength: this.correctiveFactorCalculator.debugArrLen,
      // correctiveFactorOrig: this.correctiveFactor,
      // correctiveFactorBaseline: debugCorrectiveFactorBaseline,
      ...forDebugging,
    })
    // If base is the most recent update we have, use its state.
    // Else, interpolate between its state and the state of (base + 1).
    if (base < 0) {
      // haven't received it yet
      return this.gameUpdates[this.gameUpdates.length - 1]
    } else if (base === this.gameUpdates.length - 1) {
      // need to extrapolate
      const baseUpdate = this.gameUpdates[base]
      const deltaTime = serverTime - baseUpdate.t
      // console.log(deltaTime)
      // if (deltaTime < 100) {
      // RENDER_DELAY.value = Math.min(RENDER_DELAY.value + 4, 60)
      // }
      forDebugging.timesExtrapolated++
      if (deltaTime > 20000) {
        console.log("deltaTime doesn't make set, resetting")
        this.reset()
      }
      return extrapolateState(baseUpdate, deltaTime)
    } else {
      // able to interpolate
      // RENDER_DELAY.value = RENDER_DELAY.value - 16
      const baseUpdate = this.gameUpdates[base]
      const next = this.gameUpdates[base + 1]
      const r = (serverTime - baseUpdate.t) / (next.t - baseUpdate.t)
      const interpolated = interpolateState(baseUpdate, next, r)
      // log(interpolated)
      forDebugging.timesInterpolated++
      return interpolated
    }
  }
}
